import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  Option,
  Alert,
  CircularProgress,
  Chip
} from '@mui/joy';
import {
  Add,
  Search,
  FilterList,
  Clear
} from '@mui/icons-material';
import { Note, NoteQuery, PRIORITY_DISPLAY_NAMES, VISIBILITY_DISPLAY_NAMES } from '../types/note';
import NoteCard from './NoteCard';
import NoteForm from './NoteForm';
import { noteService } from '../services/noteService';

interface NotesListProps {
  className?: string;
  initialQuery?: NoteQuery;
  showFilters?: boolean;
  showPagination?: boolean;
  compact?: boolean;
}

const NotesList: React.FC<NotesListProps> = ({
  className,
  initialQuery = {},
  showFilters = true,
  showPagination = true,
  compact = false
}) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<NoteQuery>(initialQuery);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError(null);
            
      const response = await noteService.getNotes({
        ...query,
        page: query.page || 1,
        limit: query.limit || 10
      });
            
      setNotes(response.notes);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [query]);

  const handleQueryChange = (field: keyof NoteQuery, value: any) => {
    setQuery(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setQuery(prev => ({ ...prev, page }));
  };

  const handleCreateNote = async (data: any) => {
    try {
      setSubmitting(true);
      await noteService.createNote(data);
      await fetchNotes();
      setFormOpen(false);
    } catch (err) {
      console.error('Error creating note:', err);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateNote = async (data: any) => {
    if (!editingNote) return;
    
    try {
      setSubmitting(true);
      await noteService.updateNote(editingNote.id, data);
      await fetchNotes();
      setEditingNote(null);
    } catch (err) {
      console.error('Error updating note:', err);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (note: Note) => {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await noteService.deleteNote(note.id);
      await fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note');
    }
  };

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
  };

  const handleSubmit = async (data: any) => {
    if (editingNote) {
      await handleUpdateNote(data);
    } else {
      await handleCreateNote(data);
    }
  };

  const clearFilters = () => {
    setQuery(initialQuery);
  };

  const hasActiveFilters = () => {
    return Object.keys(query).some(key => 
      key !== 'page' && key !== 'limit' && query[key as keyof NoteQuery] !== undefined
    );
  };

  return (
    <Box className={className}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography level="h3" component="h2">
          Notes
        </Typography>
        <Button
          startDecorator={<Add />}
          onClick={() => setFormOpen(true)}
        >
          Add Note
        </Button>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList />
            <Typography level="body-sm" fontWeight="md">
              Filters
            </Typography>
            {hasActiveFilters() && (
              <Button
                size="sm"
                variant="outlined"
                startDecorator={<Clear />}
                onClick={clearFilters}
              >
                Clear
              </Button>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Input
              size="sm"
              placeholder="Search notes..."
              value={query.search || ''}
              onChange={(e) => handleQueryChange('search' as keyof NoteQuery, e.target.value)}
              startDecorator={<Search />}
              sx={{ minWidth: 200 }}
            />
            
            <FormControl size="sm" sx={{ minWidth: 120 }}>
              <FormLabel>Priority</FormLabel>
              <Select
                value={query.priority || ''}
                onChange={(_, value) => handleQueryChange('priority', value || undefined)}
                placeholder="All priorities"
              >
                <Option value="">All priorities</Option>
                <Option value="normal">{PRIORITY_DISPLAY_NAMES.normal}</Option>
                <Option value="important">{PRIORITY_DISPLAY_NAMES.important}</Option>
                <Option value="urgent">{PRIORITY_DISPLAY_NAMES.urgent}</Option>
              </Select>
            </FormControl>
            
            <FormControl size="sm" sx={{ minWidth: 120 }}>
              <FormLabel>Visibility</FormLabel>
              <Select
                value={query.visibility || ''}
                onChange={(_, value) => handleQueryChange('visibility', value || undefined)}
                placeholder="All visibility"
              >
                <Option value="">All visibility</Option>
                <Option value="public">{VISIBILITY_DISPLAY_NAMES.public}</Option>
                <Option value="private">{VISIBILITY_DISPLAY_NAMES.private}</Option>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Notes List */}
      {!loading && !error && (
        <>
          {notes.length === 0 ? (
            <Alert variant="soft" color="neutral">
              No notes found. {hasActiveFilters() && 'Try adjusting your filters.'}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onEdit={handleEditNote}
                  onDelete={handleDeleteNote}
                  compact={compact}
                />
              ))}
            </Box>
          )}

          {/* Pagination */}
          {showPagination && pagination.pages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
              <Button
                size="sm"
                variant="outlined"
                disabled={pagination.page <= 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </Button>
              <Typography level="body-sm" color="neutral">
                Page {pagination.page} of {pagination.pages}
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                disabled={pagination.page >= pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </Button>
            </Box>
          )}

          {/* Results Summary */}
          {notes.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Typography level="body-sm" color="neutral">
                Showing {notes.length} of {pagination.total} notes
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Note Form Modal */}
      <NoteForm
        open={formOpen || !!editingNote}
        onClose={() => {
          setFormOpen(false);
          setEditingNote(null);
        }}
        onSubmit={handleSubmit}
        note={editingNote || undefined}
        loading={submitting}
      />
    </Box>
  );
};

export default NotesList; 